import { Badge, Button, ButtonBase, CircularProgress, Collapse, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, Typography } from "@material-ui/core";
import MailOutline from "@material-ui/icons/MailOutline";
import ExpandMore from "@material-ui/icons/ExpandMore";
import FiberManualRecord from "@material-ui/icons/FiberManualRecord";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import Alert from "@material-ui/lab/Alert";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { notificationRequest } from "../Utils/notifications";

export default function NotificationMenu() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [archive, setArchive] = useState(false);
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [unread, setUnread] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [beforeId, setBeforeId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState("");
  const mounted = useRef(false);
  const loading = useRef(false);
  const saving = useRef(false);
  const dialogOpen = useRef(false);
  const archiveView = useRef(false);
  const requestVersion = useRef(0);
  const load = useCallback(async (cursor, archiveMode = archiveView.current, force = false) => {
    if ((loading.current && !force) || saving.current) return;
    const version = ++requestVersion.current;
    loading.current = true;
    setFetching(true);
    try {
      const result = await notificationRequest("get_notifications", { archive: archiveMode, ...(cursor ? { beforeId: cursor } : {}) });
      if (!mounted.current || version !== requestVersion.current) return;
      setItems((previous) => {
        if (cursor) return previous.concat(result.notifications);
        // Keep messages read during this dialog session visible until it closes.
        const retained = archiveMode ? [] : previous.filter((item) => item.readAt && !result.notifications.some((row) => row.id === item.id));
        return result.notifications.concat(retained).sort((a, b) => b.id - a.id);
      });
      setUnread(result.unreadCount);
      setHasMore(result.hasMore);
      setBeforeId(result.notifications.length ? result.notifications[result.notifications.length - 1].id : null);
      setError("");
    } catch (err) { if (mounted.current && version === requestVersion.current) setError(err.message); }
    finally {
      if (version === requestVersion.current) {
        loading.current = false;
        if (mounted.current) setFetching(false);
      }
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    load();
    const timer = setInterval(() => { if (!document.hidden && !dialogOpen.current) load(); }, 60000);
    return () => { mounted.current = false; clearInterval(timer); };
  }, [load]);
  const close = () => {
    dialogOpen.current = false;
    archiveView.current = false;
    requestVersion.current++;
    loading.current = false;
    setOpen(false); setArchive(false); setExpanded({}); setItems([]);
    setHasMore(false); setBeforeId(null); setFetching(false); setError("");
  };
  const switchView = () => {
    const nextArchive = !archiveView.current;
    archiveView.current = nextArchive;
    setArchive(nextArchive); setItems([]); setExpanded({}); setHasMore(false); setBeforeId(null);
    load(undefined, nextArchive, true);
  };
  const updateReadState = async (item, read = true) => {
    if (saving.current || loading.current) return;
    saving.current = true;
    setBusy(true);
    const throughId = items[0].id;
    try {
      const result = await notificationRequest(read ? "mark_notifications_read" : "mark_notification_unread", item ? { notificationId: item.id }
        : { all: true, throughId });
      if (!mounted.current) return;
      setItems((previous) => previous.map((row) => (item ? row.id === item.id : row.id <= throughId)
        ? { ...row, readAt: read ? row.readAt || new Date().toISOString() : null } : row));
      if (!read) setExpanded((previous) => ({ ...previous, [item.id]: false }));
      setUnread(result.unreadCount);
      setError("");
    } catch (err) { if (mounted.current) setError(err.message); }
    finally { saving.current = false; if (mounted.current) setBusy(false); }
  };
  const toggleNotification = (item) => {
    if (saving.current || loading.current) return;
    const opening = !expanded[item.id];
    setExpanded((previous) => ({ ...previous, [item.id]: opening }));
    if (opening && !item.readAt) updateReadState(item);
  };
  const deleteNotification = async (item) => {
    if (saving.current || loading.current) return;
    saving.current = true;
    setBusy(true);
    try {
      const result = await notificationRequest("delete_notification", { notificationId: item.id });
      if (!mounted.current) return;
      setItems((previous) => previous.filter((row) => row.id !== item.id));
      setExpanded((previous) => {
        const next = { ...previous };
        delete next[item.id];
        return next;
      });
      setUnread(result.unreadCount);
      setError("");
    } catch (err) { if (mounted.current) setError(err.message); }
    finally { saving.current = false; if (mounted.current) setBusy(false); }
  };
  return <>
    <IconButton color="inherit" aria-label={t("notifications.title")} disabled={busy} onClick={() => { dialogOpen.current = true; setOpen(true); load(undefined, false, true); }}>
      <Badge badgeContent={unread} color="secondary" max={99}><MailOutline /></Badge>
    </IconButton>
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{t(archive ? "notifications.archiveTitle" : "notifications.title")}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" action={<Button onClick={() => load()}>{t("notifications.retry")}</Button>}>{error}</Alert>}
        {!items.length && !error && !fetching && <Typography>{t(archive ? "notifications.empty" : "notifications.emptyUnread")}</Typography>}
        {items.map((item) => <div key={item.id} style={{ padding: "12px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ButtonBase
              style={{ flex: 1, minWidth: 0, justifyContent: "flex-start", textAlign: "left" }}
              aria-expanded={Boolean(expanded[item.id])}
              aria-controls={`notification-message-${item.id}`}
              disabled={busy || fetching}
              onClick={() => toggleNotification(item)}
            >
              <ExpandMore style={{ flexShrink: 0, transform: expanded[item.id] ? "rotate(180deg)" : undefined }} />
              <Typography variant="subtitle1" style={{ fontWeight: item.readAt ? 400 : 700, overflowWrap: "anywhere" }}>{item.title}</Typography>
              {!item.readAt && <FiberManualRecord color="primary" role="img" aria-label={t("notifications.unread")}
                style={{ fontSize: 10, marginLeft: 8, flexShrink: 0 }} />}
            </ButtonBase>
          </div>
          <Collapse in={Boolean(expanded[item.id])} unmountOnExit>
            <div id={`notification-message-${item.id}`} style={{ marginTop: 8 }}>
              <Typography variant="caption" color="textSecondary">{new Date(item.createdAt).toLocaleString()}</Typography>
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", marginTop: 8 }}>{item.body}</Typography>
              {item.readAt && <Button size="small" disabled={busy || fetching} onClick={() => updateReadState(item, false)}>
                {t("notifications.markUnread")}
              </Button>}
              <Button size="small" color="secondary" startIcon={<DeleteOutline />} disabled={busy || fetching}
                onClick={() => deleteNotification(item)}>{t("notifications.delete")}</Button>
            </div>
          </Collapse>
          <Divider style={{ marginTop: 12 }} />
        </div>)}
        {hasMore && <Button disabled={busy || fetching} onClick={() => load(beforeId)}>{t("notifications.loadMore")}</Button>}
        {(busy || fetching) && <CircularProgress size={20} />}
      </DialogContent>
      <DialogActions>
        <Button disabled={busy || fetching} onClick={switchView}>{t(archive ? "notifications.backToInbox" : "notifications.archive")}</Button>
        <Button disabled={busy || fetching || !unread || !items.length} onClick={() => updateReadState()}>{t("notifications.markAllRead")}</Button>
        <Button onClick={close}>{t("notifications.close")}</Button>
      </DialogActions>
    </Dialog>
  </>;
}
